import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@maestra/core/store/store';
import { atualizarAddons, fetchPlanConfig, fetchSubscriptionStatus, type BillingCycle } from '@maestra/core/store/slices/subscription';
import { precoDaAssinatura } from '@maestra/core/nucleo/precoDaAssinatura';
import { formataBytes } from '@maestra/core/nucleo/cotaDeArmazenamento';
import { decisaoDoAjuste, planoMudou } from '@maestra/core/nucleo/ajusteDoPlano';
import { useCoupon } from '@maestra/core/hooks/useCoupon';

import { message } from '../../ui-carbon/padroes/avisos';
import { fmtBRL, linhaDoAdicional, type LinhaDeAdicional } from './useCheckoutDaAssinatura';
import { useEscolhaDosPerfis } from '../perfis/useEscolhaDosPerfis';

// AJUSTAR O PLANO (/planos/ajustar), sem tela. Cópia literal de `pages/Subscription/AjustarPlano`:
// semeia uma vez do agendado (ou do contratado), o cupom em vigor aparece aplicado e a troca é
// explícita, a previsão usa o preço do ASSINANTE com o desconto (como o servidor), a regra do
// ajuste é a do núcleo (`ajusteDoPlano`, a mesma da edge), as guardas de redução são ditas antes
// do clique, e a mensagem de sucesso diz o que de fato aconteceu. Ciclo não se troca aqui.
//
// QUAIS PERFIS LIBERAR (QA de pagamentos, C5; decisões do Lucas, 05/10/2026): com perfis
// travados, contratar mais pergunta qual liberar, e a escolha vai no mesmo ajuste para o servidor
// gravar. "Incluir este perfil na assinatura" chega aqui com `?incluir=<perfil>`: já com um perfil a
// mais (sobre o que está em vigor) e com ESTE marcado. A pessoa não reveza o PRO entre os perfis
// que tem: liberar outro é sempre contratar mais.
//
// A SUBIDA PROPORCIONAL E A REDUÇÃO NA DATA CERTA (A6, M9 e M10, 05/10/2026): subir libera na hora
// e a próxima cobrança vem com a diferença proporcional dos dias que faltam; reduzir não pede mais
// para apagar perfil (pergunta quais ficam, entre os ativos) e vale no fim do período pago. No
// anual a subida fica com o suporte (o anual saiu da venda).

const dataCurta = (iso: string | null) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return null;
  }
};

export const useAjustarPlano = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const incluir = params.get('incluir');
  const escolha = useEscolhaDosPerfis();

  const {
    plan, status, initialized, value, cycle, planoTravadoNoBanco,
    extraProfiles: contratadosPerfis, extraStorageGb: contratadosGb,
    profilesInQuota, storageUsedBytes, discountPercent, couponCode,
    scheduledExtraProfiles, scheduledExtraStorageGb, acessoAte, valorAteAReducao,
    precoBaseMensal, precoBaseAnual,
  } = useAppSelector((s) => s.subscription);

  const [perfis, setPerfis] = useState(0);
  const [gb, setGb] = useState(0);
  const [semeado, setSemeado] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const cupom = useCoupon();
  const [removeuCupom, setRemoveuCupom] = useState(false);
  const trocouCupom = cupom.applied !== null || removeuCupom;

  useEffect(() => {
    dispatch(fetchPlanConfig());
    dispatch(fetchSubscriptionStatus());
  }, [dispatch]);

  useEffect(() => {
    if (!initialized || semeado) return;
    // Veio incluir um perfil travado: um a mais sobre o que está EM VIGOR (com uma redução agendada,
    // partir dela só desfaria o agendamento, e o perfil seguiria travado).
    setPerfis(incluir ? contratadosPerfis + 1 : (scheduledExtraProfiles ?? contratadosPerfis));
    setGb(scheduledExtraStorageGb ?? contratadosGb);
    setSemeado(true);
  }, [initialized, semeado, contratadosPerfis, contratadosGb, scheduledExtraProfiles, scheduledExtraStorageGb, incluir]);

  const perfisInclusos = plan?.includedProfiles ?? 1;
  const gbInclusos = plan?.includedStorageGb ?? 30;
  const ciclo: BillingCycle = cycle === 'YEARLY' ? 'YEARLY' : 'MONTHLY';
  const anual = ciclo === 'YEARLY';

  // O preço DESTA assinatura, o mesmo que o servidor usa (QA de pagamentos, A3): quem assinou
  // antes de um reajuste segue no preço de quando assinou enquanto a assinatura existir. A tabela
  // só quando a linha não tem preço gravado.
  const baseMensal = precoBaseMensal ?? plan?.monthlyValue ?? 57.9;
  // Quem já é anual segue no anual mesmo com ele fora da venda: o preço dele continua o do anual.
  const baseAnual = anual || plan?.annualEnabled ? (precoBaseAnual ?? plan?.annualValue ?? null) : null;

  const composicao = useMemo(() => ({
    ciclo,
    mensal: baseMensal,
    anual: baseAnual,
    valorPorPerfil: plan?.profileAddonValue ?? 27.9,
    valorPorGb: plan?.storageAddonValuePerGb ?? 7.9,
  }), [ciclo, baseMensal, baseAnual, plan?.profileAddonValue, plan?.storageAddonValuePerGb]);

  const pctVigente = cupom.applied ? cupom.applied.discountPercent : (removeuCupom ? 0 : discountPercent);
  const codigoVigente = cupom.applied ? cupom.applied.code : (removeuCupom ? null : couponCode);
  const comDesconto = (bruto: number) =>
    pctVigente > 0 ? Math.round(bruto * (1 - pctVigente / 100) * 100) / 100 : bruto;
  const bruto = precoDaAssinatura({ ...composicao, perfisExtras: perfis, gbExtras: gb });
  const novoValor = comDesconto(bruto);
  const abatimento = Math.round((bruto - novoValor) * 100) / 100;
  // O que se paga HOJE: com redução agendada, `value` já é o preço da próxima cobrança (M10).
  const hoje = valorAteAReducao ?? value ?? comDesconto(precoDaAssinatura({ ...composicao, perfisExtras: contratadosPerfis, gbExtras: contratadosGb }));
  const dimPerfis = { contratado: contratadosPerfis, agendado: scheduledExtraProfiles, pedido: perfis };
  const dimGb = { contratado: contratadosGb, agendado: scheduledExtraStorageGb, pedido: gb };
  const mudou = planoMudou([dimPerfis, dimGb]) || trocouCupom;
  const aumentou = decisaoDoAjuste(dimPerfis) === 'aumenta' || decisaoDoAjuste(dimGb) === 'aumenta';
  const reduziu = decisaoDoAjuste(dimPerfis) === 'reduz' || decisaoDoAjuste(dimGb) === 'reduz';

  const tetoDePerfis = perfisInclusos + perfis;
  // Perfis acima do novo teto não impedem (M9): a pessoa escolhe quais ficam. Espaço sim: arquivo
  // não se trava. No anual, a subida fica com o suporte.
  const impedimento = anual && aumentou
    ? 'No plano anual, perfis e espaço a mais são incluídos pelo suporte por enquanto. Fale com a gente.'
    : gb < contratadosGb && storageUsedBytes > (gbInclusos + gb) * 1024 ** 3
      ? `Você usa ${formataBytes(storageUsedBytes)} e está pedindo ${gbInclusos + gb} GB. Libere espaço em Arquivos antes de reduzir.`
      : null;
  const precisaEscolherQuemFica = perfis < contratadosPerfis && escolha.ativos.length > tetoDePerfis;

  const enviar = async (escolhidos?: { liberar?: string[]; manter?: string[] }) => {
    const liberar = escolhidos?.liberar;
    const manter = escolhidos?.manter;
    setEnviando(true);
    try {
      const r = await dispatch(atualizarAddons({
        extraProfiles: perfis,
        extraStorageGb: gb,
        ...(trocouCupom ? { couponCode: cupom.applied?.code ?? '' } : {}),
        ...(liberar && liberar.length ? { liberar } : {}),
        ...(manter && manter.length ? { manter } : {}),
      })).unwrap();
      if (r.perfisLiberados === false) {
        message.warning('O plano foi ajustado, mas os perfis liberados foram os mais antigos. Fale com o suporte para trocar.');
      }
      await dispatch(fetchSubscriptionStatus());
      message.success(
        r.desfezAgendamento
          ? 'Agendamento cancelado. Seu plano segue como está, sem mudança na próxima cobrança.'
          : r.trocouCupom && !r.aplicadoAgora && !r.agendadoParaProximoCiclo
            ? (r.couponCode
                ? `Cupom ${r.couponCode} aplicado. O valor novo entra na próxima cobrança.`
                : 'Cupom removido. O valor novo entra na próxima cobrança.')
          : r.aplicadoAgora && r.agendadoParaProximoCiclo
            ? `Plano ajustado. O que aumentou já está valendo${r.proporcional ? ` (a próxima cobrança traz ${fmtBRL(r.proporcional)} proporcionais aos dias que faltam)` : ''}; a redução vale ${r.reducaoValeEm ? `em ${dataCurta(r.reducaoValeEm)}` : 'no fim do período pago'}.`
            : r.aplicadoAgora
              ? `Plano ajustado e já valendo. A próxima cobrança vem com o valor novo${r.proporcional ? ` e ${fmtBRL(r.proporcional)} proporcionais aos dias que faltam neste ciclo` : ''}.`
              : `Redução agendada para ${r.reducaoValeEm ? dataCurta(r.reducaoValeEm) : 'o fim do período pago'}. Até lá tudo segue como está, e o valor menor vale a partir dessa data.`,
      );
      navigate('/planos');
    } catch (e) {
      message.error(typeof e === 'string' ? e : 'Não foi possível ajustar o plano.');
    } finally {
      setEnviando(false);
    }
  };

  // Mais perfis com perfis travados na conta: quais liberar (os mais antigos, ou o que veio incluir,
  // já vêm marcados).
  const aMais = Math.max(0, perfis - contratadosPerfis);
  const confirmar = () => {
    // Reduzir abaixo dos perfis ativos: quais ficam (M9), entre os ativos.
    if (precisaEscolherQuemFica) {
      escolha.abrir({
        candidatos: escolha.ativos,
        limite: tetoDePerfis,
        titulo: 'Quais perfis ficam ativos?',
        texto: `Com ${tetoDePerfis} ${tetoDePerfis === 1 ? 'perfil' : 'perfis'} na assinatura, os outros ficam travados quando a redução valer, no fim do período que você já pagou. Nada é apagado, e eles voltam quando você contratar mais perfis.`,
        acao: 'Confirmar a redução',
        aoConfirmar: (ids) => enviar({ manter: ids }),
      });
      return;
    }
    // A lista de perfis ainda chegando (abriu o ajuste direto): o perfil que veio incluir vai junto.
    if (aMais > 0 && escolha.travados.length === 0 && incluir) {
      void enviar({ liberar: [incluir] });
      return;
    }
    if (aMais > 0 && escolha.travados.length > 0) {
      const quantos = Math.min(aMais, escolha.travados.length);
      escolha.abrir({
        candidatos: escolha.travados,
        limite: quantos,
        primeiro: incluir,
        titulo: quantos === 1 ? 'Qual perfil você quer liberar?' : 'Quais perfis você quer liberar?',
        texto: `Com ${aMais === 1 ? 'o perfil a mais' : `os ${aMais} perfis a mais`}, ${quantos === 1 ? 'um perfil travado volta' : `${quantos} perfis travados voltam`} a funcionar com o PRO. Escolha ${quantos === 1 ? 'qual' : 'quais'}.`,
        acao: 'Confirmar e liberar',
        aoConfirmar: (ids) => enviar({ liberar: ids }),
      });
      return;
    }
    void enviar();
  };

  const emAtraso = status === 'overdue';

  const adicionais: LinhaDeAdicional[] = [
    linhaDoAdicional({
      id: 'perfis', titulo: 'Perfis de artista', incluso: perfisInclusos, total: perfisInclusos + perfis,
      mudar: (total) => setPerfis(Math.max(0, total - perfisInclusos)), max: perfisInclusos + 50, passo: 1,
      valorUnitario: composicao.valorPorPerfil, unidade: ['perfil', 'perfis'],
      nota: profilesInQuota > perfisInclusos + perfis
        ? { alerta: true, texto: `Você tem ${profilesInQuota} perfis. Com esta quantidade, ${profilesInQuota - perfisInclusos - perfis} ficam inacessíveis (nada é apagado).` }
        : undefined,
    }, anual),
    linhaDoAdicional({
      id: 'armazenamento', titulo: 'Armazenamento', incluso: gbInclusos, total: gbInclusos + gb,
      mudar: (total) => setGb(Math.max(0, total - gbInclusos)), max: gbInclusos + 2000, passo: 10,
      valorUnitario: composicao.valorPorGb, unidade: ['GB', 'GB'],
      nota: { alerta: false, texto: `Você usa ${formataBytes(storageUsedBytes)} hoje.` },
    }, anual),
  ];

  return {
    // Cancelada com o PRO até o fim do período (C3): não há mais cobrança a ajustar.
    estado: !initialized ? 'carregando' as const
      : status === 'active' && acessoAte ? 'cancelada' as const
      : status !== 'active' ? (emAtraso ? 'emAtraso' as const : 'semAssinatura' as const)
        : planoTravadoNoBanco ? 'pelosuporte' as const : 'pronto' as const,
    voltar: () => navigate('/planos'),
    irParaPagamentos: () => navigate('/pagamentos'),
    irParaSuporte: () => navigate('/suporte'),
    adicionais,
    cupom: {
      valor: cupom.input,
      mudar: cupom.setInput,
      aplicar: () => { setRemoveuCupom(false); cupom.apply('subscription', bruto); },
      tirar: () => { cupom.clear(); setRemoveuCupom(true); },
      validando: cupom.loading,
      erro: cupom.error || null,
      aplicado: codigoVigente ? `${codigoVigente} · −${fmtBRL(abatimento)} (${Math.round(pctVigente)}%)` : null,
      // Tirar o cupom da assinatura é para sempre se ele venceu ou esgotou (aplicar de novo passa
      // pela validação). A tela avisa antes e, até confirmar, deixa manter (QA do pagamento).
      aviso: couponCode && !removeuCupom && !cupom.applied ? 'Se tirar este cupom, ele só volta se ainda estiver valendo.' : null,
      manter: removeuCupom && couponCode && !cupom.applied ? { rotulo: `Manter o cupom ${couponCode}`, fazer: () => setRemoveuCupom(false) } : null,
    },
    item: { nome: 'Maestra PRO', detalhe: anual ? 'Assinatura anual' : 'Assinatura mensal', preco: fmtBRL(novoValor) },
    linhas: [
      ...(abatimento > 0 ? [
        { rotulo: 'Subtotal', valor: fmtBRL(bruto), forte: false },
        {
          rotulo: codigoVigente ? `Cupom ${codigoVigente} (−${Math.round(pctVigente)}%)` : `Desconto (−${Math.round(pctVigente)}%)`,
          valor: `− ${fmtBRL(abatimento)}`,
          forte: false,
        },
      ] : []),
      { rotulo: 'Hoje você paga', valor: fmtBRL(hoje), forte: false },
      { rotulo: 'Passa a pagar', valor: fmtBRL(novoValor), forte: true },
      // Sem a linha "A partir de": o vencimento gravado pode já ser o do mês seguinte (a Asaas cria a
      // cobrança 40 dias antes), e a data da redução é o fim do período pago, que o servidor diz na
      // confirmação. A explicação abaixo diz quando cada coisa vale.
    ],
    explicacao: mudou
      ? `${aumentou ? 'O que você adicionou fica disponível agora. A próxima cobrança vem com o valor novo e a diferença proporcional dos dias que faltam neste ciclo. ' : ''}${reduziu ? 'A redução vale no fim do período que você já pagou: até lá tudo segue como está, e o valor menor vale a partir daí.' : ''}`.trim()
      : 'Escolha as quantidades para ver o novo valor.',
    mudou,
    impedimento,
    enviando,
    confirmar,
    escolha,
  };
};

export type DadosDoAjuste = ReturnType<typeof useAjustarPlano>;
